import { Box, Flex, Text, VStack, Link } from '@chakra-ui/react';
import React from 'react';

import type { WalletType } from 'types/client/wallets';
import { SUPPORTED_WALLETS } from 'types/client/wallets';

import detectWallet from 'lib/web3/detectWallet';
import useDetectWalletEip6963 from 'lib/web3/useDetectWalletEip6963';
import { WALLETS_INFO } from 'lib/web3/wallets';
import { DialogBody, DialogContent, DialogHeader, DialogRoot, DialogCloseTrigger } from 'toolkit/chakra/dialog';
import IconSvg from 'ui/shared/IconSvg';

interface Props {
  open: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSelect: (wallet: WalletType, provider: any) => void;
}

const WalletSelectorModal = ({ open, onOpenChange, onSelect }: Props) => {
  const { detect } = useDetectWalletEip6963();
  const [ detectedWallets, setDetectedWallets ] = React.useState<Partial<Record<WalletType, any>>>({});

  React.useEffect(() => {
    if (open) {
      const checkWallets = async() => {
        try {
          // Trigger EIP-6963 detection once for all wallets
          window.dispatchEvent(new Event('eip6963:requestProvider'));

          // Wait a bit for all providers to announce themselves
          await new Promise((resolve) => setTimeout(resolve, 300));

          const results: Partial<Record<WalletType, any>> = {};
          for (const wallet of SUPPORTED_WALLETS) {
            try {
              const eip6963 = await detect(wallet);
              if (eip6963) {
                results[wallet] = eip6963.provider;
              } else {
                const legacy = detectWallet(wallet);
                if (legacy) {
                  results[wallet] = legacy.provider;
                }
              }
            } catch (e) {
              console.error(`Error detecting wallet ${ wallet }:`, e);
            }
          }
          setDetectedWallets(results);
        } catch (error) {
          console.error('Error during wallet detection:', error);
        }
      };
      checkWallets();
    }
  }, [ open, detect ]);

  return (
    <DialogRoot open={ open } onOpenChange={ onOpenChange } size="md">
      <DialogContent>
        <DialogHeader>Select Wallet</DialogHeader>
        { /* <DialogCloseTrigger /> */ }
        <DialogBody pb={ 6 }>
          <VStack align="stretch" gap={ 3 }>
            { SUPPORTED_WALLETS.map((wallet) => {
              const info = WALLETS_INFO[wallet];
              const provider = detectedWallets[wallet];
              const isDetected = Boolean(provider);

              return (
                <Flex
                  key={ wallet }
                  p={ 4 }
                  borderWidth="1px"
                  borderRadius="md"
                  align="center"
                  cursor={ isDetected ? 'pointer' : 'default' }
                  opacity={ isDetected ? 1 : 0.6 }
                  _hover={ isDetected ? { bg: 'bg.muted', borderColor: info.color } : {} }
                  onClick={ () => isDetected && onSelect(wallet, provider) }
                  justify="space-between"
                >
                  <Flex align="center" gap={ 3 }>
                    <IconSvg name={ info.icon } boxSize={ 6 }/>
                    <Text fontWeight="600">{ info.name }</Text>
                  </Flex>
                  { !isDetected && (
                    <Text fontSize="xs" color="fg.muted">
                      Not detected
                    </Text>
                  ) }
                  { isDetected && (
                    <Box boxSize={ 2 } borderRadius="full" bg="green.500"/>
                  ) }
                </Flex>
              );
            }) }
          </VStack>
          <Text mt={ 6 } fontSize="sm" textAlign="center" color="fg.muted">
            Don't see your wallet? Make sure it's installed and unlocked.
          </Text>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export default WalletSelectorModal;
